import { FC, useState } from 'react';
import styles from './MyPublicationCard.module.scss';
import { images } from '../../constants/images';
import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import FavoriteIcon from '@mui/icons-material/Favorite';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import TouchAppOutlinedIcon from '@mui/icons-material/TouchAppOutlined';
import EditIcon from '@mui/icons-material/Edit';
import PauseIcon from '@mui/icons-material/Pause';
import CloseIcon from '@mui/icons-material/Close';
import SettingsIcon from '@mui/icons-material/Settings';
import Tooltip from '@mui/material/Tooltip';
import { MyPublicationCardProps } from './interfaces';
import { useNavigate } from 'react-router-dom';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';

const MyPublicationCard: FC<MyPublicationCardProps> = ({
    publication,
    onEdit,
    onPause,
    onDelete,
    onDeleteClick,
    onClick,
    visits = 0,
    confirmedVisits = 0,
    interactions = 0,
    isFavorited = false,
    onFavoritedChange,
    isPaused = false
}) => {
    const [localIsFavorited, setLocalIsFavorited] = useState(isFavorited);
    const [isExpanded, setIsExpanded] = useState(false);
    const navigate = useNavigate();

    const formatPrice = (value?: number) => {
        if (!value) return '$0';
        return `$${value.toLocaleString('es-CL')}`;
    };

    const imageUrl = publication?.pictures?.[0]?.path || images.noImagePublication;
    const title = publication?.title || 'Sin título';
    const year = publication?.vehicle_data?.year || publication?.primary_description?.split(' ')[0] || '';
    const mileage = publication?.vehicle_data?.mileage || publication?.secondary_description || '0';
    const price = publication?.price;
    const publicationId = publication?._id;
    const status = publication?.paused ? 'paused' : (publication?.status_brief?.code === 'pending' ? 'pending' : publication?.status_brief?.code === 'match' ? 'match' : publication?.status_brief?.code === 'payment_made' ? 'payment_made' : 'active');
    const isPending = publication?.status_brief?.code === 'pending';
    const isMatch = publication?.status_brief?.code === 'match';
    const isPaymentMade = publication?.status_brief?.code === 'payment_made';

    const handleFavoritToggle = (e: React.MouseEvent) => {
        e.stopPropagation();
        const newState = !localIsFavorited;
        setLocalIsFavorited(newState);
        onFavoritedChange?.(newState);
    };

    const handleEdit = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (isPending || isMatch || isPaused || isPaymentMade) {
            return;
        }
        onEdit?.(publicationId);
    };

    const handlePause = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (isPending || isMatch || isPaymentMade) {
            return;
        }
        onPause?.(publicationId);
    };

    const handleDelete = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (isPending || isMatch || isPaused || isPaymentMade) {
            return;
        }
        onDeleteClick?.(publication);
    };

    const handleGestion = (e: React.MouseEvent) => {
        e.stopPropagation();
        navigate(`/perfil/mis_publicaciones/${publicationId}/gestion`);
    };

    const handleCardClick = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (isPending || isMatch || isPaused || isPaymentMade) {
            return;
        }
        setIsExpanded(!isExpanded);
    };

    const handleExpandClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        setIsExpanded(!isExpanded);
    };

    return (
        <div 
            className={`${styles.myPublicationCard} ${isExpanded ? styles.expanded : ''}`}
            onClick={handleCardClick}
            onDragStart={(e) => e.preventDefault()}
        >
            <div className={styles.cardColumns}>
                {/* Image Container */}
                <div className={styles.imageContainer}>
                    <img 
                        src={imageUrl} 
                        alt={title}
                        className={styles.vehicleImage}
                    />
                </div>

                {/* Content Container */}
                <div className={styles.contentContainer}>
                    {/* Header */}
                    <div className={styles.header}>
                        <div className={styles.titleSection}>
                            <h3 
                                className={styles.title}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    if (isPending || isMatch || isPaused || isPaymentMade) {
                                        return;
                                    }
                                    if (publicationId) {
                                        navigate(`/publicacion/${publicationId}`);
                                    }
                                }}
                            >
                                {title}
                            </h3>
                            <div className={styles.subtitle}>
                                <span>{year}</span>
                                <span className={styles.separator}>|</span>
                                <span>{mileage} km</span>
                            </div>
                        </div>

                        <div className={styles.priceSection}>
                            <p className={styles.price}>{formatPrice(price)}</p>
                            <div className={`${styles.confirmedBadge} ${status === 'paused' ? styles.pausedBadge : status === 'pending' ? styles.pendingBadge : status === 'match' ? styles.matchBadge : status === 'payment_made' ? styles.paymentMadeBadge : ''}`}>
                                <span>{
                                    status === 'paused' ? 'Pausada' : 
                                    status === 'pending' ? 'Pendiente' : 
                                    status === 'match' ? 'Match' : 
                                    status === 'payment_made' ? 'Pago realizado' : 
                                    'Activa'
                                }</span>
                            </div>
                        </div>
                    </div>

                    {/* Status Badge */}
                    <div className={ styles.hiddenSection }>      {/* va a ser asi className={`${styles.visitInfo} ${isPending ? styles.hiddenSection : ''}`} cuando existan estos datos  */}
                        <div className={styles.statusBadge}>
                            <CalendarTodayIcon className={styles.statusIcon} />
                            <span>
                                <span>
                                    <span>
                                <span className={styles.boldText}>{visits} visitas</span> · <span className={styles.mediumText}>{confirmedVisits} confirmada{confirmedVisits !== 1 ? 's' : ''}</span>
                            </span>
                                </span>
                            </span>
                        </div>
                    </div>

                    {/* Info Section with indicators and actions */}
                    <div className={styles.infoSection}>
                        <div className={styles.indicatorsContainer}>
                            <Tooltip 
                                title="Vistas" 
                                arrow 
                                placement="top"
                                slotProps={{
                                    popper: {
                                        modifiers: [
                                            {
                                                name: 'offset',
                                                options: {
                                                    offset: [0, -6],
                                                },
                                            },
                                        ],
                                    },
                                    tooltip: {
                                        sx: {
                                            backgroundColor: '#111',
                                            color: '#fff',
                                            fontFamily: "'Poppins', sans-serif",
                                            fontSize: '11px',
                                            fontWeight: 500,
                                            padding: '4px 8px',
                                            borderRadius: '6px',
                                        },
                                    },
                                }}
                            >
                                <div className={styles.indicator}>
                                    <RemoveRedEyeOutlinedIcon className={styles.indicatorIcon} />
                                    <span className={styles.indicatorText}>{visits}</span>
                                </div>
                            </Tooltip>
                            <span className={styles.indicatorSeparator}>|</span>
                            <Tooltip 
                                title="Me gusta" 
                                arrow 
                                placement="top"
                                slotProps={{
                                    popper: {
                                        modifiers: [
                                            {
                                                name: 'offset',
                                                options: {
                                                    offset: [0, -6],
                                                },
                                            },
                                        ],
                                    },
                                    tooltip: {
                                        sx: {
                                            backgroundColor: '#111',
                                            color: '#fff',
                                            fontFamily: "'Poppins', sans-serif",
                                            fontSize: '11px',
                                            fontWeight: 500,
                                            padding: '4px 8px',
                                            borderRadius: '6px',
                                        },
                                    },
                                }}
                            >
                                <div className={styles.indicator}>
                                    <button 
                                        className={styles.favoriteButton}
                                        onClick={handleFavoritToggle}
            
                                    >
                    
                                            <FavoriteBorderIcon className={styles.indicatorIcon} />
                                        
                                    </button>
                                    <span className={styles.indicatorText}>34</span>
                                </div>
                            </Tooltip>
                            <span className={styles.indicatorSeparator}>|</span>
                            <Tooltip 
                                title="Interacciones" 
                                arrow 
                                placement="top"
                                slotProps={{
                                    popper: {
                                        modifiers: [
                                            {
                                                name: 'offset',
                                                options: {
                                                    offset: [0, -6],
                                                },
                                            },
                                        ],
                                    },
                                    tooltip: {
                                        sx: {
                                            backgroundColor: '#111',
                                            color: '#fff',
                                            fontFamily: "'Poppins', sans-serif",
                                            fontSize: '11px',
                                            fontWeight: 500,
                                            padding: '4px 8px',
                                            borderRadius: '6px',
                                        },
                                    },
                                }}
                            >
                                <div className={styles.indicator}>
                                    <TouchAppOutlinedIcon className={styles.indicatorIcon} />
                                    <span className={styles.indicatorText}>{interactions}</span>
                                </div>
                            </Tooltip>
                        </div> 

                        {/* Actions Section */}
                        <div className={styles.actionsContainer}>
                            <button 
                                className={styles.actionButton}
                                onClick={handleGestion}
                                title="Ir a gestión"
                            >
                                <SettingsIcon style={{ fontSize: 18, marginRight: 4 }} />
                                Gestión
                            </button>

                            <span className={styles.actionSeparator}>|</span>

                            <button 
                                className={`${styles.actionButton} ${isPending || isMatch || isPaused || isPaymentMade ? styles.disabledAction : ''}`}
                                onClick={handleEdit}
                                title="Editar publicación"
                            >
                                <EditIcon style={{ fontSize: 18, marginRight: 4 }} />
                                Editar
                            </button>

                            <span className={styles.actionSeparator}>|</span>

                            <button 
                                className={`${styles.actionButton} ${isPending || isMatch || isPaymentMade ? styles.disabledAction : ''}`}
                                onClick={handlePause}
                                title={isPaused ? "Reanudar publicación" : "Pausar publicación"}
                            >
                                {isPaused ? (
                                    <PlayArrowIcon style={{ fontSize: 18, marginRight: 4 }} />
                                ) : (
                                    <PauseIcon style={{ fontSize: 18, marginRight: 4 }} />
                                )}
                                {isPaused ? 'Activar' : 'Pausar'}
                            </button>

                            <span className={styles.actionSeparator}>|</span>

                            <button 
                        className={`${styles.actionButton} ${styles.deleteButton} ${isPending || isMatch || isPaused || isPaymentMade ? styles.disabledAction : ''}`}
                        onClick={handleDelete}
                        title="Eliminar publicación"
                    >
                                <CloseIcon style={{ fontSize: 18, marginRight: 4 }} />
                                Eliminar
                            </button>
                        </div>
                    </div>
                </div>

                {/* Expand Button */}
                <button 
                    className={`${styles.expandButton} ${isExpanded ? styles.expanded : ''}`}
                    onClick={handleExpandClick}
                >
                    <KeyboardArrowDownIcon />
                </button>
            </div>
        </div>
    );
};

export default MyPublicationCard;
